import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Dialog, DialogPanel } from "@headlessui/react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import DensityMediumRoundedIcon from "@mui/icons-material/DensityMediumRounded";
import { Button, Text, DropDown, CartsView, ChangeLanguage } from "@components";
import { Storage } from "@utils";
import c2hLogo from "@assets/images/c2hLogo.png";
import defaultUser from "@assets/images/defaultUser.png";
import useScrollingHeader from "../../hooks/useScrollingHeader";
import { setLanguage } from "../../redux/user/slice";
import { change_Language } from "../../redux/settings/slice";
import { setIsMobileMenuOpen } from "../../redux/header/slice";
import "../../App.css";

const AuthenticationRoute = ({ onPressLogin, onPressSignup, isScrolled }) => {
  const { t } = useTranslation("common");
  return (
    <div className="flex flex-row items-center justify-between gap-x-3">
      <Button
        onClick={onPressLogin}
        variant="primary"
        className={`${
          window.location.pathname !== "/signup" ? AuthActive : AuthInactive
        }`}
      >
        <Text
          className={` ${
            window.location.pathname !== "/signup" ? AuthText : InactiveText
          }  px-2`}
        >
          {t("auth.login")}
        </Text>
      </Button>
      <Button
        onClick={onPressSignup}
        variant="primary"
        className={`${
          window.location.pathname === "/signup" ? AuthActive : AuthInactive
        }`}
      >
        <Text
          className={`${
            window.location.pathname === "/signup" ? AuthText : InactiveText
          } px-2`}
        >
          {t("auth.signUp")}
        </Text>
      </Button>
    </div>
  );
};

const Accordion = ({ value, active, path, inActive, index, data }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex flex-col w-[100%]">
      <div className="flex  w-full items-center cursor-pointer relative">
        <TopNavBar
          key={index}
          value={value}
          path={path}
          active={active}
          inActive={inActive}
        />
        {data?.length ? (
          <div className="absolute right-0">
            {open ? (
              <ExpandLessIcon
                sx={{ color: "white" }}
                onClick={() => setOpen(!open)}
              />
            ) : (
              <ExpandMoreIcon
                sx={{ color: "white" }}
                onClick={() => setOpen(!open)}
              />
            )}
          </div>
        ) : null}
      </div>
      {open && data?.length && (
        <div className={` w-full flex flex-col pr-5`} key={index}>
          {data?.map((item, ind) => {
            return (
              <div key={ind}>
                <p
                  className={`${
                    ind === 0 ? "" : "mt-3"
                  } block  pb-1 text-[16px] font-semibold text-gray-400 ml-2`}
                >
                  {item?.attributes?.title}
                </p>
                {item?.attributes?.child?.data?.map((value, i) => {
                  return (
                    <NavLink
                      to={value?.attributes?.url}
                      key={i}
                      className="my-1.5 block text-[14px] font-semibold text-gray-500 hover:text-gray-400 ml-4 "
                    >
                      {value?.attributes?.title}
                    </NavLink>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const OpenProfile = (props) => {
  const { t } = useTranslation("common");

  const options = [
    {
      index: 0,
      title: t("profile.profile"),
      value: "/profile",
    },
    {
      index: 1,
      title: t("profile.setting"),
      value: "/settings",
    },
    {
      index: 2,
      title: t("profile.logOut"),
      value: "/login",
    },
  ];

  const { userDetail } = useSelector((store) => store.authReducer);

  let fname = "User";
  let image = defaultUser;

  if (userDetail) {
    fname = userDetail?.firstname;
    image = userDetail?.image;
  }

  return (
    <div className="flex flex-row items-center pr-6 text-white ">
      <DropDown
        title={fname}
        options={options}
        titleStyle="max-w-[150px] truncate"
        image={image}
      />
    </div>
  );
};

const TopNavBar = ({ value, active, path, inActive, isScrolled }) => {
  const pathName = "/" + window.location.pathname.split("/")[1];
  return (
    <div className="w-full">
      <NavLink
        key={value}
        to={path}
        className={`${
          isScrolled
            ? "text-headerText after:bg-headerText"
            : "text-white after:bg-white"
        } ${
          (pathName === "/course" ? "/courses" : pathName) === path
            ? active
            : inActive
        }  ${
          (pathName === "/course" ? "/courses" : pathName) !== path &&
          "header-underline-animation h-[100%] "
        }`}
      >
        {value}
      </NavLink>
    </div>
  );
};

export default function Header({
  hideHeaderSearchBar,
  searchInputProps,
  searchMenuProps,
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { i18n, t } = useTranslation("common");
  const { menus, logo, ismobileMenuOpen } = useSelector(
    (state) => state.headerReducer
  );
  const { userDetail, userToken } = useSelector((store) => store.authReducer);
  const { language } = useSelector((store) => store.userReducer);

  const logoPath = logo?.data && logo?.data?.logo?.url;

  const headerNav = menus?.data?.filter(
    (val, ind) => val?.slug === "header-nav"
  );
  const navlinks = headerNav && headerNav[0]?.items;

  const [imageExists, setImageExists] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [announcementTxt, setAnnouncementTxt] = useState("");
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY;
      setIsScrolled(offset > 100);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const checkImageExists = () => {
    const img = new Image();
    img.src = logoPath;

    img.onload = () => {
      setImageExists(true);
    };

    img.onerror = () => {
      setImageExists(false);
    };
  };

  React.useEffect(() => {
    checkImageExists();
  }, [logoPath]);

  useEffect(() => {
    const announcementTxt = logo?.data?.announcementText;
    if (announcementTxt) {
      setAnnouncementTxt(announcementTxt);
    }
  }, [logo]);

  useEffect(() => {
    dispatch(setIsMobileMenuOpen(mobileMenuOpen));
  }, [mobileMenuOpen]);

  const onPressLogin = () => navigate("/login");

  const onPressSignup = (e) => {
    e?.stopPropagation();
    navigate("/signup");
  };

  const handleLanguageChange = (props) => {
    dispatch(setLanguage(props));
    i18n.changeLanguage(props.language);
    Storage.save(Storage.LANGUAGE, props.language);
    dispatch(change_Language(props.language));
  };

  return (
    <div className={`w-full h-auto flex flex-col fixed top-0 z-50`}>
      <nav>
        {announcementTxt && !ismobileMenuOpen && (
          <div
            className={`w-full  ${
              isScrolled ? "bg-white" : "bg-primary"
            } flex justify-center py-[6px]`}
          >
            <button
              className={`font-black underline text-center underlin underline-offset-2 hover:opacity-70  ${
                isScrolled ? "text-primary" : "text-btnTextColor"
              }`}
              onClick={() => {
                navigate("/courses");
              }}
            >
              {announcementTxt}
            </button>
          </div>
        )}
      </nav>

      <div
        className={`w-full h-auto  ${
          isScrolled ? "bg-headerColor" : "bg-transparent"
        } flex flex-row items-center justify-between lg:px-20 md:px-10 xsm:px-4 px-2 xsm:gap-x-4 gap-x-2 pt-4 pb-4 ${
          ismobileMenuOpen ? "hidden" : "flex"
        }`}
      >
        <div
          className={` py-2 xl:py-0 w-[15%] sm:min-w-[190px] xsm:min-w-[140px] min-w-[120px]`}
        >
          <NavLink to="/" className="webkitHighlight">
            <img
              className="img-fluid"
              src={imageExists ? logoPath : c2hLogo}
              alt=""
            />
          </NavLink>
        </div>

        <div
          className={
            " xl2a:flex hidden flex-row items-center justify-between w-[85%]"
          }
        >
          <div className="flex flex-row items-center justify-center w-[50%]">
            {navlinks && navlinks?.length > 0 ? (
              navlinks?.map((item, index) => (
                <div
                  className="w-full flex items-center justify-center menu-hover text-center py-[4px] h-[100%]"
                  key={index}
                >
                  <TopNavBar
                    key={index}
                    value={item?.title}
                    path={item?.url}
                    active={ActiveTab}
                    inActive={InactiveTab}
                    isScrolled={isScrolled}
                  />
                </div>
              ))
            ) : (
              <></>
            )}
          </div>

          <div className="w-[50%] xl:flex flex-row gap-x-3 hidden justify-end items-cente ">
            <CartsView />
            {/* {userDetail && userToken ? (
              <OpenProfile navigate={navigate} dispatch={dispatch} />
            ) : (
              <AuthenticationRoute
                onPressSignup={onPressSignup}
                onPressLogin={onPressLogin}
                isScrolled={isScrolled}
              />
            )} */}
            <ChangeLanguage
              title={language}
              titleStyle="max-w-[150px] truncate pt-2"
              handleLanguageChange={handleLanguageChange}
            />
          </div>
        </div>

        <div
          className={`xl2a:hidden  flex flex-row items-end justify-center xsm:gap-x-2 gap-x-1 ${
            ismobileMenuOpen ? "hidden" : "flex"
          }`}
        >
          <CartsView />

          <Button
            style={{ backgroundColor: "rgba(18,16,55,0.30)" }}
            variant="primary"
            className="rounded-md text-white h-10 w-10 webkitHighlight"
            onClick={() => {
              dispatch(setIsMobileMenuOpen(true));
              setMobileMenuOpen(true);
            }}
          >
            <DensityMediumRoundedIcon aria-hidden="true" />
          </Button>
        </div>
      </div>

      <Dialog
        as="div"
        className="xl2a:hidden flex"
        open={ismobileMenuOpen}
        onClose={setMobileMenuOpen}
      >
        <div className="fixed inset-0 z-[9]" />
        <DialogPanel className={SmDrawer}>
          <div className="flex items-center justify-between  mt-6">
            {/* <div className="flex justify-center webkitHighlight">
              {userDetail && userToken ? (
                <OpenProfile navigate={navigate} dispatch={dispatch} />
              ) : (
                <AuthenticationRoute
                  onPressSignup={onPressSignup}
                  onPressLogin={onPressLogin}
                />
              )}
            </div> */}

            <Button
              className="-m-2.5 rounded-md p-2.5 text-gray-700 webkitHighlight"
              onClick={() => {
                dispatch(setIsMobileMenuOpen(false));
                setMobileMenuOpen(false);
              }}
              variant="primary"
            >
              <ClearRoundedIcon
                className="h-6 w-6 text-btnTextColor"
                aria-hidden="true"
                fontSize="large"
              />
            </Button>
          </div>
          <div className="mt-7 ">
            <ChangeLanguage
              title={language}
              titleStyle="max-w-[150px] truncate"
              handleLanguageChange={handleLanguageChange}
            />
          </div>
          <div className="mt-6 flow-root">
            <div className="-my-6 divide-y divide-gray-500/10">
              <div className="space-y-2 py-6">
                {navlinks && navlinks?.length > 0 ? (
                  navlinks?.map((item, index) => (
                    <Accordion
                      key={index}
                      value={item?.title}
                      path={item?.url}
                      active={MobActive}
                      inActive={MobInactive}
                      data={item?.children}
                    />
                  ))
                ) : (
                  <></>
                )}
              </div>
            </div>
          </div>
        </DialogPanel>
      </Dialog>
    </div>
  );
}

const ActiveTab = `
text-[17px] 
font-sans
font-black
underline
underline-offset-4
decoration-1
self-center
hover:cursor-default
text-center
 `;

const InactiveTab = `
font-sans
text-center
text-[17px] 
self-center
font-semibold`;

const AuthActive = `
lg:px-4 
px-2 
py-[8px] 
bg-primary 
rounded-full 
flex 
items-center 
justify-center
`;

const AuthInactive = `
lg:px-4 
px-2 
py-[8px] 
bg-white 
rounded-full 
flex 
items-center 
justify-center`;

const SmDrawer = `
fixed 
inset-y-0 
right-0 
z-10 
w-full 
overflow-y-auto 
bg-[#589ed6]
px-6 
py-6 
sm:max-w-sm 
sm:ring-1 sm:ring-gray-900/10`;

const MobActive = `
text-[18px] 
-mx-3 
block 
rounded-lg 
px-3 
py-2 
font-sans
text-base 
font-bold 
leading-7 
text-primary 
border-b-2 
border-b-primary
`;

const MobInactive = `
text-[18px] 
-mx-3 
font-sans
block 
rounded-lg 
px-3 
py-2 
text-base 
font-semibold 
leading-7 
text-[white]
`;

const AuthText = `
text-btnTextColor 
text-[17px] 
font-bold 
font-sans
`;

const InactiveText = `
text-[#004996] 
text-[17px] 
font-bold 
font-sans
`;
